import { useTranslation } from "@msuite/ui/localization";
import { type RefetchFn, useQuery } from "@msuite/ui/query";
import { type DataTableVariant, getDataTableComponent } from "@msuite/ui/table";
import { Tabs } from "@msuite/ui/tabs";
import { responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";
import { SuborderArticlesTable } from "../suborder-articles-table";
import { SuborderLoadCarriersTable } from "../suborder-load-carriers-table";
import { columns } from "./table-config";

/** Props Interface */
interface SubordersTableProps {
	orderId: string;
	type: DataTableVariant;
	refetch: RefetchFn;
}

/**
 * The rounds an order was split into, each expanding into the work it holds.
 *
 * A round carries two kinds of line and neither implies the other, so the expanded row is tabbed
 * rather than a single table: articles are what is fetched off the shelves of its zone, and load
 * carriers are the boxes they travel in — plus the empties an order asked for as Leergut, which is
 * the only work a round without articles has at all.
 *
 * The tabs need a `panelKey` of their own, because the pages this table appears on carry a `Tabs`
 * of their own on the default key: sharing it would move the page's tab whenever a row's tab was
 * clicked. Every expanded row reads that one key, so all of them open on the same tab, which is
 * what a reader comparing two rounds wants.
 */
export function SubordersTable({ orderId, type }: SubordersTableProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { data, refetch, isLoading, queryKey } = useQuery({
		queryKey: ["suborders", orderId],
		queryFn: async () => {
			const response = await client.stock.orders[":orderId"].suborders.$get({ param: { orderId } });
			const data = await responseHandler(response);
			return data.suborders;
		},
	});

	/** Computed */
	const Table = getDataTableComponent(type);

	/** Render */
	return (
		<Table
			data={data}
			columns={columns as any}
			isLoading={isLoading}
			refetch={refetch}
			queryKey={queryKey}
			persistenceKey="suborders-table"
			enableRowSelection
			enableMultiRowSelection
			menu={null}
			getRowCanExpand={() => true}
			// The `end` array is the render order of the pinned region, left to right. The manager's
			// release/recall control sits after the progress meter so it is the last column before the
			// row actions, which the DataTable keeps at the very end of the region itself.
			initialPinningState={{
				start: [],
				end: ["progress", "action"],
			}}
			renderSubRows={(row) => {
				return (
					<Tabs
						variant="sub-table"
						className="stripes"
						withHeader={false}
						panelKey="suborder-row-tabs"
						groups={[
							{
								name: "suborderWork",
								panels: [
									{
										label: t("stock.articles"),
										errors: [],
										cyId: "suborder-row-articles-tab",
										panel: () => (
											<SuborderArticlesTable
												suborderId={row.original.id}
												orderId={row.original.orderId}
											/>
										),
									},
									{
										label: t("stock.load_carriers"),
										errors: [],
										cyId: "suborder-row-load-carriers-tab",
										panel: () => (
											<SuborderLoadCarriersTable
												suborderId={row.original.id}
												orderId={row.original.orderId}
											/>
										),
									},
								],
							},
						]}
					/>
				);
			}}
		/>
	);
}
